import { useUserData } from '@flik/shared';
import { useTheme } from '@flik/shared/components';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { useSessions } from '../hooks/useSessions';
import MagicModal from '../magic/components/MagicModal';

const DashboardPage: React.FC = () => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const { activeSessions, inactiveSessions, isLoading, isError } =
    useSessions();
  const { firstName } = useUserData();

  // Helper function to format date
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60),
    );

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInHours < 48) return 'Yesterday';
    if (diffInHours < 168)
      return `${Math.floor(diffInHours / 24)} day${
        Math.floor(diffInHours / 24) > 1 ? 's' : ''
      } ago`;
    return date.toLocaleDateString();
  };

  // Helper function to handle session click
  const handleSessionClick = (sessionId: string) => {
    navigate(`/sessions/${sessionId}`);
  };

  // Helper function to handle start new Flik
  const handleStartNewFlik = () => {
    navigate('/create-session');
  };

  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      {/* NavBar */}
      <NavBar />

      {/* Magic Modal at the top without initial offset */}
      <MagicModal />

      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1
          className={`text-5xl md:text-6xl font-light ${provideTailwind(
            'text-primary',
          )}`}
        >
          Welcome back {firstName || 'there'}
        </h1>
      </div>

      {/* Recent Fliks - Combined Active and Inactive Sessions */}
      <div className="max-w-4xl mx-auto px-6 mb-16">
        <div className="flex items-center justify-between mb-8">
          <h2
            className={`text-2xl font-light ${provideTailwind('text-primary')}`}
          >
            Recent Fliks
          </h2>
          <button
            className={`inline-flex items-center px-6 py-3 rounded-full text-base font-medium transition-all duration-200 ${provideTailwind(
              'bg-primary-button',
            )} ${provideTailwind('hover:bg-primary-button')} ${provideTailwind(
              'text-primary-button',
            )} shadow-lg hover:shadow-xl transform hover:scale-105`}
            onClick={handleStartNewFlik}
          >
            <span className="mr-2">+</span>
            Start New Flik
          </button>
        </div>
        {isLoading ? (
          <div
            className={`text-center py-8 ${provideTailwind('text-secondary')}`}
          >
            Loading sessions...
          </div>
        ) : isError ? (
          <div
            className={`text-center py-8 ${provideTailwind('text-red-500')}`}
          >
            Error loading sessions
          </div>
        ) : activeSessions.length === 0 && inactiveSessions.length === 0 ? (
          <div
            className={`text-center py-8 ${provideTailwind('text-secondary')}`}
          >
            No recent sessions
          </div>
        ) : (
          <div className="space-y-3">
            {/* Active Sessions First */}
            {activeSessions.map((session) => (
              <div
                key={session.id}
                className={`group cursor-pointer transition-all duration-200 hover:scale-[1.01]`}
                onClick={() => handleSessionClick(session.id)}
              >
                <div
                  className={`${provideTailwind(
                    'bg-secondary/30',
                  )} rounded-2xl p-4 border-0 hover:${provideTailwind(
                    'bg-secondary/50',
                  )} transition-all duration-200`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <h3
                        className={`text-base font-medium mb-1 ${provideTailwind(
                          'text-primary',
                        )}`}
                      >
                        {session.title}
                      </h3>
                      <p
                        className={`text-sm ${provideTailwind(
                          'text-tertiary',
                        )}`}
                      >
                        {formatDate(session.created_on)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium px-3 py-1 rounded-full bg-green-500/20 text-green-500">
                        Active
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Inactive Sessions After */}
            {inactiveSessions.slice(0, 8).map((session) => (
              <div
                key={session.id}
                className={`group cursor-pointer transition-all duration-200 hover:scale-[1.01]`}
                onClick={() => handleSessionClick(session.id)}
              >
                <div
                  className={`${provideTailwind(
                    'bg-secondary/30',
                  )} rounded-2xl p-4 border-0 hover:${provideTailwind(
                    'bg-secondary/50',
                  )} transition-all duration-200`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <h3
                        className={`text-base font-medium mb-1 ${provideTailwind(
                          'text-primary',
                        )}`}
                      >
                        {session.title}
                      </h3>
                      <p
                        className={`text-sm ${provideTailwind(
                          'text-tertiary',
                        )}`}
                      >
                        {formatDate(
                          session.last_updated_on || session.created_on,
                        )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium px-3 py-1 rounded-full bg-gray-500/20 text-gray-500">
                        Past
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
